import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import MLMTreeVisualization from './MLMTreeVisualization';
import MLMSidePanel from './MLMSidePanel';
import { orgChartService } from '../../services/orgChartService';

/**
 * OrgChartMLMModal Component
 *
 * Large modal that shows full MLM network tree for a selected org chart position's user.
 * Provides "deep dive" exploration of MLM networks from org chart view.
 *
 * Features:
 * - Full MLM tree visualization (left 70%)
 * - Performance side panel (right 30%)
 * - Click nodes to view performance
 * - Search users in MLM network
 * - Auto-navigation to searched users
 * - Independent state from parent org chart
 *
 * @param {boolean} visible - Modal visibility state
 * @param {function} onHide - Callback when modal is closed
 * @param {object} positionData - Selected position data with user info
 */
const OrgChartMLMModal = ({ visible, onHide, positionData }) => {
    const [treeData, setTreeData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Lazy Loading State
    const [expandingUserId, setExpandingUserId] = useState(null);
    const expandNodeRef = useRef(null);

    // Side Panel State
    const [selectedNodeId, setSelectedNodeId] = useState(null);
    const [nodeData, setNodeData] = useState(null);
    const [nodeLoading, setNodeLoading] = useState(false);
    const [nodeError, setNodeError] = useState(null);

    // Search State (for in-canvas search via MLMTreeVisualization)
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [isSearchSelection, setIsSearchSelection] = useState(false);

    const toast = useRef(null);

    // Get current user info
    const userInfo = JSON.parse(localStorage.getItem('user') || '{}');
    const currentUserId = userInfo.id;

    // Fetch MLM tree when modal opens (SHALLOW for lazy loading)
    useEffect(() => {
        if (visible && positionData?.position?.id) {
            fetchMLMTreeShallow(positionData.position.id);
        }
    }, [visible, positionData]);

    /**
     * Fetch shallow MLM tree for the selected position (LAZY LOADING)
     * Matches MLMTreePage.js pattern
     */
    const fetchMLMTreeShallow = async (positionId) => {
        console.log('OrgChartMLMModal: Fetching shallow MLM tree for position:', positionId);
        setLoading(true);
        setError(null);

        try {
            const response = await orgChartService.getPositionUserMLMTreeShallow(positionId);

            if (response.success) {
                console.log('OrgChartMLMModal: Shallow tree loaded:', response.data);
                setTreeData(response.data);

                // Fetch initial node performance for root user (only once when tree loads)
                if (positionData?.position?.user?.id) {
                    fetchNodePerformance(positionData.position.user.id);
                }
            } else {
                setError(response.message || 'Failed to load MLM tree');
            }
        } catch (err) {
            console.error('OrgChartMLMModal: Error fetching shallow MLM tree:', err);
            const errorMessage = err.message || 'Failed to load MLM tree';
            setError(errorMessage);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: errorMessage,
                life: 5000
            });
        } finally {
            setLoading(false);
        }
    };

    /**
     * Load children for a node (LAZY LOADING)
     * Matches MLMTreePage.js loadNodeChildren() pattern
     * Security: Same as node performance - validates user in org chart branch MLM network
     */
    const loadNodeChildren = useCallback(async (userId) => {
        console.log('OrgChartMLMModal: Loading children for user:', userId);
        setExpandingUserId(userId);

        try {
            // Call children endpoint without limit to get ALL direct children
            // No positionId needed - uses same security as node performance
            const response = await orgChartService.getPositionUserMLMChildren(
                userId,
                0,
                null  // null = get ALL children
            );

            if (response.success) {
                console.log('OrgChartMLMModal: Children loaded:', response.data.children);
                updateTreeWithChildren(userId, response.data.children);

                // Wait for React to commit state, then expand the node (100ms like MLM)
                setTimeout(() => {
                    if (expandNodeRef.current) {
                        console.log('OrgChartMLMModal: Expanding user after data load:', userId);
                        expandNodeRef.current(userId);
                    } else {
                        console.warn('OrgChartMLMModal: expandNodeRef.current is null - cannot expand node');
                    }
                }, 100);
            } else {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: response.message || 'Failed to load children',
                    life: 3000
                });
            }
        } catch (error) {
            console.error('OrgChartMLMModal: Error loading children:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.response?.data?.message || error.message || 'Failed to load children',
                life: 3000
            });
        } finally {
            setExpandingUserId(null);
        }
    }, []);

    /**
     * Update tree data with loaded children
     * Matches MLMTreePage.js pattern (adapted for single root)
     */
    const updateTreeWithChildren = useCallback((userId, children) => {
        console.log('OrgChartMLMModal: Updating tree with children for user:', userId, children);

        setTreeData(prevTree => {
            if (!prevTree) return prevTree;

            const updatedTree = JSON.parse(JSON.stringify(prevTree)); // Deep clone

            // Recursive function to find and update the node
            const updateNode = (node) => {
                if (node.id === userId) {
                    // Found the node - update its children
                    node.children = children;
                    console.log('OrgChartMLMModal: Updated node children:', node);
                    return true;
                }

                // Recursively search in children
                if (node.children && node.children.length > 0) {
                    for (let i = 0; i < node.children.length; i++) {
                        if (updateNode(node.children[i])) {
                            return true;
                        }
                    }
                }

                return false;
            };

            updateNode(updatedTree);
            return updatedTree;
        });
    }, []);

    /**
     * Fetch node performance data
     */
    const fetchNodePerformance = async (userId) => {
        console.log('OrgChartMLMModal: fetchNodePerformance called with userId:', userId);
        setNodeLoading(true);
        setNodeError(null);
        setSelectedNodeId(userId);

        try {
            const response = await orgChartService.getNodePerformance(userId);

            console.log('OrgChartMLMModal: API response:', response);

            if (response.success) {
                setNodeData(response.data);
            } else {
                setNodeError(response.message || 'Failed to load performance data');
            }
        } catch (err) {
            console.error('OrgChartMLMModal: Error fetching node performance:', err);
            const errorMessage = err.message || 'Failed to load performance data';
            setNodeError(errorMessage);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: errorMessage,
                life: 3000
            });
        } finally {
            setNodeLoading(false);
        }
    };

    /**
     * Handle node click from tree visualization
     */
    const handleNodeClick = (nodeId) => {
        console.log('OrgChartMLMModal: handleNodeClick called with nodeId:', nodeId);
        if (nodeId) {
            setIsSearchSelection(false); // Regular node click, NOT from search
            fetchNodePerformance(nodeId);
        }
    };

    /**
     * Handle in-canvas search selection (from floating search panel)
     */
    const handleInCanvasSearchSelect = (userId) => {
        console.log('OrgChartMLMModal: handleInCanvasSearchSelect called with userId:', userId);
        setSelectedUserId(userId);
        setIsSearchSelection(true); // THIS IS A SEARCH SELECTION - trigger path finder
        fetchNodePerformance(userId);
    };

    /**
     * Custom search function for MLMTreeVisualization (searches via org chart API)
     */
    const customSearchFunction = useCallback(async (query, limit) => {
        if (!positionData?.position?.user?.id) {
            return { success: false, data: { results: [] } };
        }
        return orgChartService.searchUserMLMViaOrgChart(
            positionData.position.user.id,
            query,
            limit
        );
    }, [positionData?.position?.user?.id]);

    /**
     * Reset state when modal closes
     */
    const handleHide = () => {
        setTreeData(null);
        setNodeData(null);
        setSelectedNodeId(null);
        setError(null);
        setNodeError(null);
        setSelectedUserId(null);
        setIsSearchSelection(false);
        onHide();
    };

    return (
        <>
            <Toast ref={toast} />
            <Dialog
                visible={visible}
                onHide={handleHide}
                header={
                    <div className="flex align-items-center gap-2">
                        <i className="pi pi-share-alt text-primary text-lg md:text-xl"></i>
                        <span className="text-sm md:text-base font-semibold white-space-nowrap overflow-hidden text-overflow-ellipsis">
                            MLM Network - {positionData?.position?.user?.name || 'User'}
                        </span>
                    </div>
                }
                style={{ width: '95vw', height: '90vh' }}
                breakpoints={{ '768px': '100vw', '576px': '100vw' }}
                maximizable
                modal
                className="org-chart-mlm-modal"
            >
                {/* Loading State */}
                {loading && (
                    <div className="flex align-items-center justify-content-center" style={{ minHeight: '60vh' }}>
                        <div className="text-center">
                            <ProgressSpinner />
                            <p className="mt-3 text-600">Loading MLM network tree...</p>
                        </div>
                    </div>
                )}

                {/* Error State */}
                {error && !loading && (
                    <Message severity="error" text={error} className="w-full" />
                )}

                {/* Main Content: Tree + Side Panel - Same structure as OrgChartPage */}
                {!loading && !error && treeData && (
                    <div className="flex flex-column lg:flex-row gap-2 md:gap-3 flex-1 overflow-hidden org-mlm-modal-content" style={{ height: 'calc(90vh - 80px)' }}>
                        {/* Left: Tree - No extra Card wrapper */}
                        <div className="surface-card border-round shadow-1 overflow-hidden org-mlm-tree-container" style={{ minHeight: '300px' }}>
                            <MLMTreeVisualization
                                treeData={treeData}
                                currentUserId={positionData?.position?.user?.id}
                                selectedUserId={selectedUserId}
                                isSearchSelection={isSearchSelection}
                                loading={false}
                                onNodeClick={handleNodeClick}
                                onLoadChildren={loadNodeChildren}
                                expandingNodeId={expandingUserId}
                                expandNodeRef={expandNodeRef}
                                onSearchSelect={handleInCanvasSearchSelect}
                                customSearchFunction={customSearchFunction}
                            />
                        </div>

                        {/* Right: Side Panel - p-3 like OrgChart */}
                        <div className="surface-card border-round shadow-1 overflow-auto p-3 org-mlm-panel-container">
                            <MLMSidePanel
                                nodeData={nodeData}
                                loading={nodeLoading}
                                error={nodeError}
                                accessType="orguser"
                            />
                        </div>
                    </div>
                )}

                {/* Responsive CSS for modal layout */}
                <style>{`
                    .org-mlm-modal-content {
                        flex-direction: column;
                    }
                    .org-mlm-tree-container {
                        flex: 1 1 auto;
                        min-height: 50vh;
                        height: auto;
                    }
                    .org-mlm-panel-container {
                        flex: 0 0 auto;
                        max-height: 35vh;
                    }

                    @media (min-width: 992px) {
                        .org-mlm-modal-content {
                            flex-direction: row;
                        }
                        .org-mlm-tree-container {
                            flex: 0 0 65%;
                            min-height: auto;
                            height: 100%;
                        }
                        .org-mlm-panel-container {
                            flex: 0 0 35%;
                            max-height: none;
                            height: 100%;
                        }
                    }
                `}</style>
            </Dialog>
        </>
    );
};

export default OrgChartMLMModal;
